"use client";

import { useState } from "react";
import { getSession, signIn } from "next-auth/react";
import { useQuery } from "@apollo/client";
import { GET_PARAMETERS } from "@/data/queries/user.gql";
import { useRouter } from "next/navigation";
import React from "react";
import {
  LocalStorageService,
  LOCAL_ADMIN_PARAMETERS,
  LAST_ACTIVITY,
} from "@/utils/local-data";
import { LastActivityObj } from "@/hooks/useIdleApp";
import toast from "react-hot-toast";

export function getUserLocal() {
  // Only run on client side to avoid localStorage server-side error
  if (typeof window !== "undefined") {
    const users = localStorage.getItem("users");
    return users ? JSON.parse(users) : [];
  }
  return [];
}

function saveUserLocal(user) {
  // Only run on client side to avoid localStorage server-side error
  if (typeof window !== "undefined") {
    const users = localStorage.getItem("users");
    const newUserLocal = {
      first_name: user.first_name,
      last_name: user.last_name,
      dni: user.dni,
      id: user.id,
    };
    const usersArray = users ? JSON.parse(users) : [];
    const userSaved = usersArray.find((u) => u.id === user?.id);
    if (!userSaved) {
      usersArray.push(newUserLocal);
      localStorage.setItem("users", JSON.stringify(usersArray));
    }
  }
}

export interface UseLoginReturn {
  signIn: () => Promise<void>;
  disabled: boolean;
  user: string;
  setUser: React.Dispatch<React.SetStateAction<string>>;
  setPassword: React.Dispatch<React.SetStateAction<string>>;
  modal: Record<string, string | boolean> | undefined;
  setModal: React.Dispatch<
    React.SetStateAction<Record<string, string | boolean> | undefined>
  >;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  isSubmitting: boolean;
}

export function useLogin(
  t: (text: string, params?: any) => string,
  ip: string,
): UseLoginReturn {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<Record<string, string | boolean>>();
  const { data: parameters } = useQuery(GET_PARAMETERS);
  const router = useRouter();

  const redirectUser = (session) => {
    const user = session.user;
    if (user?.change_password_in_acccess && !user?.last_access) {
      setModal({
        title: t("user.first_income.title"),
        description: t("user.first_income.description"),
        resetPassword: true,
      });
      return;
    } else if (user?.change_password_in_acccess && user?.last_access) {
      setModal({
        title: t("user.expired_password.title"),
        description: t("user.expired_password.description"),
        resetPassword: true,
      });
      return;
    } else if (user) {
      saveUserLocal(user);
      localStorage.setItem("isLogin", "true");
      router.replace("/app");
    }
  };

  const handleErrors = (err) => {
    const minutes = parameters?.getParameters?.login_min_attemps_minutes;
    const attemps = err?.extensions?.info?.attemps;
    const state = err?.extensions?.info?.lock_state?.toLowerCase?.();

    if (err?.code === "AddressNotAllowedError") {
      setModal({
        title: t(`user.error.not_allowed.title`),
        description: t(`user.error.not_allowed.description`, { ip }),
      });
    } else if (err?.message === "user_not_exists") {
      setModal({
        title: t(`user.error.not_exist.title`),
        description: t(`user.error.not_exist.description`),
      });
    } else if (err?.message === "user_not_active") {
      setModal({
        title: t(`user.error.not_active.title`),
        description: t(`user.error.not_active.description`),
      });
    } else if (state && (!attemps || attemps == 3 || attemps > 5)) {
      setModal({
        title: t(`user.error.${state}.title`),
        description: t(`user.error.${state}.description`, {
          minutes,
          textMinutes: t("minutes"),
        }),
      });
    } else if (attemps) {
      setError(
        t(`user.error.incorrect_${attemps}`, {
          minutes,
          textMinutes: t("minutes"),
        }),
      );
    } else {
      setModal({
        title: t(`user.error.unknown.title`),
        description: t(`user.error.unknown.description`),
      });
    }
  };

  async function signInHandler() {
    setIsSubmitting(true);
    try {
      const res = await signIn("credentials", {
        dni: user,
        password: password,
        redirect: false,
        callbackUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/auth/reset`,
      });
      const session = await getSession();

      if (res?.ok) {
        LocalStorageService.saveData(
          LOCAL_ADMIN_PARAMETERS,
          parameters.getParameters,
        );
        LocalStorageService.saveData<LastActivityObj>(LAST_ACTIVITY, {
          date: new Date().toISOString(),
          inactivity: false,
        });
        toast.remove();
        redirectUser(session);
      } else if (res?.error) {
        let error = JSON.parse(res.error);
        handleErrors(error);
      }
    } catch (err) {
      console.log(err);
    }
    setIsSubmitting(false);
  }

  return {
    signIn: signInHandler,
    disabled: !user || !password || isSubmitting,
    user,
    setUser,
    setPassword,
    modal,
    setModal,
    error,
    setError,
    isSubmitting,
  };
}
